import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../../../../../utils/axiosInstance";
import PriceListTable from "./PriceListTable";
import PriceListForm from "./PriceListForm";
import ConfirmModal from "../../../../../components/Modals/ConfirmModal";
import { toast } from "../../../../../components/Toast/toast";

const PriceListPage = ({ clientId }) => {
  const [priceList, setPriceList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const action = searchParams.get("action");
  const editId = searchParams.get("id");

  useEffect(() => {
    fetchPriceList();
  }, [clientId]);

  const fetchPriceList = async () => {
    setLoading(true);
    try {
      const res = await api.get("/client-price-list", { withCredentials: true });
      const filtered = res.data.data.filter(
        (item) => item.client?.id === parseInt(clientId)
      );
      setPriceList(filtered);
      setError("");
    } catch (err) {
      console.error("Error fetching price list:", err);
      setError("Failed to load client price list");
    } finally {
      setLoading(false);
    }
  };

  const setAction = (nextAction, id) => {
    const next = new URLSearchParams(searchParams);
    if (nextAction) {
      next.set("action", nextAction);
    } else {
      next.delete("action");
    }
    if (id !== undefined && id !== null) {
      next.set("id", String(id));
    } else {
      next.delete("id");
    }
    setSearchParams(next, { replace: true });
  };

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setShowDeleteModal(true);
  };

  const handleClone = async (item) => {
    try {
      await api.post(`/client-price-list/${item.id}/clone`, null, {
        withCredentials: true,
      });
      await fetchPriceList();
    } catch (err) {
      console.error("Error cloning price list entry:", err);
      toast.error(err.response?.data?.message || "Failed to clone price list entry");
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      await api.delete(`/client-price-list/${itemToDelete.id}`, {
        withCredentials: true,
      });
      setShowDeleteModal(false);
      setItemToDelete(null);
      fetchPriceList();
    } catch (err) {
      console.error("Error deleting price:", err);
      toast.error("Failed to delete price list entry");
    }
  };

  const handleSave = async (data) => {
    try {
      if (action === "edit" && editId) {
        await api.put(`/client-price-list/${editId}`, data, {
          withCredentials: true,
        });
      } else {
        await api.post("/client-price-list", data, { withCredentials: true });
      }
      setAction(null);
      fetchPriceList();
    } catch (err) {
      console.error("Error saving price:", err);
      toast.error(err.response?.data?.message || "Failed to save client price");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <p className="text-gray-500 text-sm animate-pulse">Loading price list...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center mt-10">
        <p className="text-red-600 mb-4 text-sm">{error}</p>
        <button
          onClick={fetchPriceList}
          className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded"
        >
          Retry
        </button>
      </div>
    );
  }

  if (action === "add" || (action === "edit" && editId)) {
    let editingItem = null;
    if (action === "edit" && editId) {
      const item = priceList.find((p) => String(p.id) === String(editId));
      if (!item) {
        return (
          <div className="text-center mt-10 text-gray-500">
            Price entry not found.
          </div>
        );
      }
      editingItem = {
        id: item.id,
        client_id: item.client_id,
        service_id: item.service?.id || item.service_id,
        language_pair_id: item.languagePair?.id || item.language_pair_id,
        specialization_id: item.specialization?.id || item.specialization_id,
        currency_id: item.currency?.id || item.currency_id,
        unit: item.unit,
        price_per_unit: item.price_per_unit,
        note: item.note || "",
      };
    }

    return (
      <PriceListForm
        clientId={clientId}
        editingItem={editingItem}
        onCancel={() => setAction(null)}
        onSave={handleSave}
      />
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
          Client Price List
        </h3>
        <button
          onClick={() => setAction("add")}
          className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded"
        >
          Add Price
        </button>
      </div>
      <PriceListTable
        data={priceList}
        onEdit={(item) => setAction("edit", item.id)}
        onDelete={handleDeleteClick}
        onClone={handleClone}
      />

      {showDeleteModal && (
        <ConfirmModal
          title="Delete Price Entry"
          message="Are you sure you want to delete this price list entry? This action cannot be undone."
          onConfirm={handleDeleteConfirm}
          onCancel={() => {
            setShowDeleteModal(false);
            setItemToDelete(null);
          }}
        />
      )}
    </div>
  );
};

export default PriceListPage;
